import { Suspense, lazy, useEffect, useState } from 'react'
import { usePostHog } from '@posthog/react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'

import type { AuthUser, FrontendConfig } from './types'
import { AuthProvider, useAuth, withoutTokenParams } from './auth/useAuth'
import { apiFetch } from './auth/apiClient'
import type { StoredUser } from './auth/tokenStore'
import { analyticsRouteFor, isPostHogEnabled } from './services/analytics'
import { AppShell } from './shell/AppShell'
import { ErrorBoundary } from './shell/ErrorBoundary'
import { ActivateLinkPage } from './shell/ActivateLinkPage'
import { LoginPage } from './shell/LoginPage'
import { PagePanel, RoutePlaceholder, ShellLoadingPanel } from './shell/PagePanel'
import { isStreetbeatEmail, navItemsForFlags, routeKind } from './shell/routeModel'
import { getFeatureFlags } from './featureFlags'
import { queryClient } from './queryClient'

const ProfileRoute = lazy(() =>
  import('./routes/ProfileRoute').then((module) => ({ default: module.ProfileRoute })),
)
const AdminUsersRoute = lazy(() =>
  import('./routes/AdminUsersRoute').then((module) => ({ default: module.AdminUsersRoute })),
)
const DonorsRoute = lazy(() =>
  import('./routes/DonorsRoute').then((module) => ({ default: module.DonorsRoute })),
)
const DonorProfileRoute = lazy(() =>
  import('./routes/DonorProfileRoute').then((module) => ({ default: module.DonorProfileRoute })),
)
const DonorPolicyRoute = lazy(() =>
  import('./donor-policies/DonorPolicyRoute').then((m) => ({ default: m.DonorPolicyRoute })),
)
const DecisionBoardRoute = lazy(() => import('./routes/decision-board/DecisionBoardRoute'))
const InboxRoute = lazy(() =>
  import('./routes/inbox/InboxRoute').then((module) => ({ default: module.InboxRoute })),
)
const DataLabRoute = lazy(() =>
  import('./data-lab/DataLabRoute').then((module) => ({ default: module.DataLabRoute })),
)
const ReportRoute = lazy(() =>
  import('./routes/ReportRoute').then((module) => ({ default: module.ReportRoute })),
)
const ExecutiveOverviewRoute = lazy(() =>
  import('./routes/ExecutiveOverviewRoute').then((module) => ({
    default: module.ExecutiveOverviewRoute,
  })),
)
const RaccoltaReportRoute = lazy(() =>
  import('./routes/RaccoltaReportRoute').then((module) => ({
    default: module.RaccoltaReportRoute,
  })),
)
const RicambioReportRoute = lazy(() =>
  import('./routes/RicambioReportRoute').then((module) => ({
    default: module.RicambioReportRoute,
  })),
)
const ValoreReportRoute = lazy(() =>
  import('./routes/ValoreReportRoute').then((module) => ({ default: module.ValoreReportRoute })),
)
const FallitiReportRoute = lazy(() =>
  import('./routes/FallitiReportRoute').then((module) => ({ default: module.FallitiReportRoute })),
)
const EconomiaCampagneReportRoute = lazy(() =>
  import('./routes/EconomiaCampagneReportRoute').then((module) => ({
    default: module.EconomiaCampagneReportRoute,
  })),
)
const BepReportRoute = lazy(() =>
  import('./routes/BepReportRoute').then((module) => ({
    default: module.BepReportRoute,
  })),
)
const OneoffDirectMailingBepRoute = lazy(() =>
  import('./routes/OneoffDirectMailingBepRoute').then((module) => ({
    default: module.OneoffDirectMailingBepRoute,
  })),
)
const OneOffCampaignLtvRoute = lazy(() =>
  import('./routes/OneOffCampaignLtvRoute').then((module) => ({
    default: module.OneOffCampaignLtvRoute,
  })),
)
const OneOffCohortDevelopmentRoute = lazy(() =>
  import('./routes/OneOffCohortDevelopmentRoute').then((module) => ({
    default: module.OneOffCohortDevelopmentRoute,
  })),
)
const HighPotentialPipelineRoute = lazy(() =>
  import('./routes/HighPotentialPipelineRoute').then((module) => ({
    default: module.HighPotentialPipelineRoute,
  })),
)
const RegularOverviewRoute = lazy(() =>
  import('./routes/PortfolioOverviewRoute').then((module) => ({
    default: module.RegularOverviewRoute,
  })),
)
const RegularAcquisitionQualityRoute = lazy(() =>
  import('./routes/RegularAcquisitionQualityRoute').then((module) => ({
    default: module.RegularAcquisitionQualityRoute,
  })),
)
const OneOffOverviewRoute = lazy(() =>
  import('./routes/PortfolioOverviewRoute').then((module) => ({
    default: module.OneOffOverviewRoute,
  })),
)
const OrganizationsOverviewRoute = lazy(() =>
  import('./routes/OrganizationsOverviewRoute').then((module) => ({
    default: module.OrganizationsOverviewRoute,
  })),
)
const GrantsRadarRoute = lazy(() =>
  import('./routes/GrantsRadarRoute').then((module) => ({
    default: module.GrantsRadarRoute,
  })),
)
export function FrontendApp({ config }: { config?: FrontendConfig }) {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <RoutedFrontendApp config={config} />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

function RoutedFrontendApp({ config }: { config?: FrontendConfig }) {
  const { user, loading, pendingLink, updatePhone } = useAuth()
  const location = useLocation()
  const currentPath = location.pathname || config?.currentPath || '/'
  const currentRoute = routeKind(currentPath)
  const featureFlags = getFeatureFlags(config?.featureFlags)
  const authUser = toAuthUser(user)

  if (!loading && pendingLink) return <ActivateLinkPage />
  if (currentRoute === 'login') {
    if (!loading && authUser) {
      const params = new URLSearchParams(location.search)
      const cliCallbackUrl = params.get('cli_callback_url')
      const cliState = params.get('cli_state')
      if (cliCallbackUrl && cliState) {
        return <CliLoginExchange redirectUrl={cliCallbackUrl} state={cliState} />
      }
      const nextParam = params.get('next')
      const dest = nextParam && nextParam.startsWith('/') ? nextParam : '/'
      return <Navigate to={dest} replace />
    }
    return <LoginPage />
  }
  if (loading) return <ShellLoadingPanel />
  if (!authUser) {
    // A spent token must not ride along into the login redirect and the next link.
    const next = encodeURIComponent(`${location.pathname}${withoutTokenParams(location.search)}`)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  return (
    <AppShell
      authUser={authUser}
      currentPath={currentPath}
      navItems={navItemsForFlags(featureFlags)}
    >
      <PostHogRouteTracker authUser={authUser} />
      <Suspense fallback={<ShellLoadingPanel />}>
        <Routes>
          {/* Every user starts with the same personal chat workspace. */}
          <Route path="/" element={<Navigate to="/chat?new=1" replace />} />
          <Route path="/chat" element={<></>} />
          <Route path="/dashboards/*" element={<Navigate to="/" replace />} />
          <Route path="/donors" element={<DonorsRoute />} />
          <Route path="/donors/:donorId" element={<DonorProfileRoute />} />
          <Route path="/pivot" element={<RedirectPreservingSearch to="/pivot/donatori" />} />
          {/* Pre-rename links shared by the agent or saved by users. */}
          <Route path="/data-lab" element={<RedirectPreservingSearch to="/pivot/donatori" />} />
          <Route path="/data-lab/:entity" element={<DataLabRedirect />} />
          <Route path="/pivot/:entity" element={<DataLabRoute />} />
          <Route
            path="/review"
            element={
              isStreetbeatEmail(authUser.email) ? (
                <DecisionBoardRoute />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/insights"
            element={
              <InboxRoute
                agentName={authUser.agent_name}
                userId={authUser.id}
                userName={authUser.name}
              />
            }
          />
          <Route path="/jobs" element={<Navigate to="/" replace />} />
          <Route path="/report" element={<ReportRoute userName={authUser.name} />} />
          <Route
            path="/report/quadro-generale"
            element={<ExecutiveOverviewRoute userName={authUser.name} />}
          />
          <Route path="/report/raccolta" element={<RaccoltaReportRoute />} />
          <Route path="/report/ricambio" element={<RicambioReportRoute />} />
          <Route path="/report/valore" element={<ValoreReportRoute />} />
          <Route path="/report/pagamenti-falliti" element={<FallitiReportRoute />} />
          <Route path="/report/economia-campagne" element={<EconomiaCampagneReportRoute />} />
          <Route path="/report/calcolo-bep" element={<BepReportRoute />} />
          <Route
            path="/report/calcolo-bep-direct-mailing"
            element={<OneoffDirectMailingBepRoute />}
          />
          <Route path="/report/performance-campagne-one-off" element={<OneOffCampaignLtvRoute />} />
          <Route
            path="/report/sviluppo-coorti-one-off"
            element={<OneOffCohortDevelopmentRoute />}
          />
          <Route path="/report/pipeline-alto-potenziale" element={<HighPotentialPipelineRoute />} />
          <Route path="/report/donatori-regolari" element={<RegularOverviewRoute />} />
          <Route
            path="/report/acquisizione-regolari"
            element={<RegularAcquisitionQualityRoute />}
          />
          <Route path="/report/donatori-one-off" element={<OneOffOverviewRoute />} />
          <Route path="/report/organizzazioni" element={<OrganizationsOverviewRoute />} />
          <Route path="/bandi" element={<GrantsRadarRoute />} />
          <Route path="/organizzazioni" element={<DonorPolicyRoute policyId="organizations" />} />
          <Route path="/donatori-regolari" element={<DonorPolicyRoute policyId="regular" />} />
          <Route path="/donatori-one-off" element={<DonorPolicyRoute policyId="oneoff" />} />
          <Route
            path="/donatori-one-off-digital"
            element={<DonorPolicyRoute policyId="digital-oneoff" />}
          />
          <Route
            path="/profile"
            element={<ProfileRoute authUser={authUser} onPhoneUpdate={updatePhone} />}
          />
          <Route
            path="/admin/users"
            element={
              authUser.is_admin ? (
                <AdminUsersRoute
                  currentUserId={authUser.id}
                  whatsappEnabled={authUser.whatsapp_enabled === true}
                />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route path="/admin/agents" element={<Navigate to="/" replace />} />
          <Route path="*" element={<RoutePlaceholder path={currentPath} />} />
        </Routes>
      </Suspense>
    </AppShell>
  )
}

function CliLoginExchange({ redirectUrl, state }: { redirectUrl: string; state: string }) {
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    apiFetch('/api/auth/cli/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ redirect_url: redirectUrl, state }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = (await response.json().catch(() => ({}))) as { detail?: unknown }
          throw new Error(typeof payload.detail === 'string' ? payload.detail : 'CLI login failed')
        }
        return response.json() as Promise<{ redirect_url: string }>
      })
      .then((payload) => {
        if (active) window.location.assign(payload.redirect_url)
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : 'CLI login failed')
      })
    return () => {
      active = false
    }
  }, [redirectUrl, state])

  if (error) {
    return (
      <PagePanel>
        <h2>CLI login failed</h2>
        <p role="alert">{error}</p>
      </PagePanel>
    )
  }
  return <ShellLoadingPanel />
}

function PostHogRouteTracker({ authUser }: { authUser: AuthUser }) {
  const location = useLocation()
  const posthog = usePostHog()

  useEffect(() => {
    if (!isPostHogEnabled || !posthog) return
    posthog.identify(`progetto-arca-user:${authUser.id}`, {
      email: authUser.email,
      name: authUser.name,
      role: authUser.role,
      is_admin: authUser.is_admin,
    })
  }, [authUser.email, authUser.id, authUser.is_admin, authUser.name, authUser.role, posthog])

  useEffect(() => {
    if (!isPostHogEnabled || !posthog) return
    const route = analyticsRouteFor(location.pathname, location.search)
    posthog.capture('$pageview', {
      $current_url: `${window.location.origin}${route.path}`,
      ...route,
      user_role: authUser.role,
      user_is_admin: authUser.is_admin,
    })
  }, [authUser.is_admin, authUser.role, location.pathname, location.search, posthog])

  return null
}

function DataLabRedirect() {
  const location = useLocation()
  const entity = location.pathname.split('/')[2] ?? 'donatori'
  return <RedirectPreservingSearch to={`/pivot/${entity}`} />
}

// Saved pivot links carry the whole slice in the query string: never drop it.
function RedirectPreservingSearch({ to }: { to: string }) {
  const location = useLocation()
  return <Navigate to={`${to}${location.search}`} replace />
}

function toAuthUser(user: StoredUser | null): AuthUser | null {
  if (!user) return null
  return {
    id: user.id ?? 0,
    email: user.email,
    name: user.name ?? user.email,
    role: user.role,
    is_admin: user.is_admin,
    phone: user.phone,
    whatsapp_enabled: user.whatsapp_enabled,
    agent_name: user.agent_name ?? 'Luma',
  }
}
